import { useLocation } from 'react-router-dom';

// Uma etapa por tela. A autenticação (o código que chega no telefone) fica separada da
// identificação de propósito: é o passo em que o paciente sai do navegador para buscar o
// código, e vê-lo contado na barra deixa claro que voltar com ele é avançar.
//
// A ordem é a do fluxo de verdade (ver os `navigate` das páginas): a entrada é paga ANTES de
// assinar o termo. Se essa ordem mudar lá, muda aqui junto — uma barra que promete a
// assinatura antes do pagamento contradiz a tela logo abaixo dela.
const STEPS = [
  { label: 'Identificação', match: /^\/identification$/ },
  { label: 'Autenticação', match: /^\/verification$/ },
  { label: 'Selecionar unidade', match: /^\/companies$/ },
  { label: 'Visualizar débitos', match: /^\/charges\/[^/]+$/ },
  { label: 'Simular acordo', match: /^\/agreement\/[^/]+\/simulation$/ },
  { label: 'Pagar entrada', match: /^\/agreement\/[^/]+\/payment$/ },
  { label: 'Assinar termo', match: /^\/agreement\/[^/]+\/term$/ },
] as const;

// A tela final não é uma etapa a cumprir: é todas cumpridas.
const DONE = /^\/agreement\/[^/]+\/success$/;

/** Em que etapa o paciente está — `STEPS.length` quando concluiu tudo, `null` fora do fluxo. */
export function currentStep(pathname: string): number | null {
  if (DONE.test(pathname)) return STEPS.length;
  const index = STEPS.findIndex((step) => step.match.test(pathname));
  return index === -1 ? null : index;
}

/**
 * O sumário das etapas no topo de toda tela: quantas já foram e quantas faltam.
 *
 * O nome de cada etapa aparece ao passar o mouse — mas a maioria chega pelo link do WhatsApp,
 * no celular, onde não existe "passar o mouse". Por isso o nome da etapa ATUAL fica escrito
 * embaixo da barra, sempre: o tooltip é conveniência de desktop, não o único jeito de saber.
 */
const StepProgress = () => {
  const { pathname } = useLocation();
  const current = currentStep(pathname);
  if (current === null) return null;

  const finished = current === STEPS.length;

  return (
    <nav aria-label="Etapas do acordo" className="mb-6">
      <ol className="flex items-center">
        {STEPS.map((step, index) => {
          const done = index < current;
          const active = index === current;
          const circle = done
            ? 'bg-red-600 text-white border-red-600'
            : active
              ? 'bg-white text-red-600 border-red-600 ring-4 ring-red-100'
              : 'bg-white text-slate-400 border-slate-300';

          return (
            <li key={step.label} className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}>
              <div className="group relative">
                <span
                  aria-current={active ? 'step' : undefined}
                  aria-label={`Etapa ${index + 1}: ${step.label}${done ? ' (concluída)' : ''}`}
                  className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold transition ${circle}`}
                >
                  {done ? '✓' : index + 1}
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-2 py-1 text-xs text-white opacity-0 shadow transition group-hover:opacity-100"
                >
                  {index + 1} · {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`mx-1 h-1 flex-1 rounded-full transition ${done ? 'bg-red-600' : 'bg-slate-200'}`}
                />
              )}
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-center text-sm text-slate-600">
        {finished
          ? 'Todas as etapas concluídas'
          : `Etapa ${current + 1} de ${STEPS.length} · ${STEPS[current]?.label ?? ""}`}
      </p>
    </nav>
  );
};

export default StepProgress;
